import React, { useCallback, useEffect, useState } from "react";
import _debounce from "lodash/debounce";
import {
  InputAdornment,
  TextField,
  TextFieldProps,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { styled } from "@mui/material";
import { setVoucherCustomAmountIsValid } from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { useDispatch } from "react-redux";

type StyledTextFieldProps = TextFieldProps & {
  valid?: boolean;
  error?: boolean;
};

const StyledTextField = styled(TextField)<StyledTextFieldProps>(
  ({ theme, valid, error }) => ({
    marginBottom: theme.spacing(2),
    width: "100%",
    zIndex: 0,
    "& .MuiInputBase-input": {
      color: error ? "#E94F54" : theme.palette.primary.main,
      borderRadius: 0,
      "&:-webkit-autofill": {
        WebkitBoxShadow: "0 0 0 100px transparent inset !important",
        boxShadow: "0 0 0 100px transparent inset !important",
        WebkitTextFillColor: error
          ? "#E94F54 !important"
          : theme.palette.primary.main + " !important",
        transition: "background-color 5000s ease-in-out 0s !important",
      },
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: error
          ? "#E94F54"
          : valid
          ? theme.palette.input.focus
          : theme.palette.primary.main,
      },
      "&:hover fieldset": {
        borderColor: error ? "#E94F54" : theme.palette.input.focus,
      },
      "&.Mui-focused fieldset": {
        borderColor: error ? "#E94F54" : theme.palette.input.focus,
      },
    },
    "& .MuiInputLabel-outlined": {
      color: error ? "#E94F54" : theme.palette.primary.main,
      opacity: 0.8,
    },
    "& .MuiFormHelperText-root": {
      color: "#E94F54",
    },
  })
);

interface DebouncedTextInputProps {
  label?: string;
  initialValue?: string;
  onChange: (value: string) => void;
  valid?: boolean;
  autoComplete?: string;
  required?: boolean;
  min?: number;
  max?: number;
}

const DebouncedNumberInput = (props: DebouncedTextInputProps) => {
  const dispatch = useDispatch();
  const { min = 5, max = 200 } = props;
  const [value, setValue] = useState<string>("");
  const [localValid, setLocalValid] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const theme = useTheme();

  const debouncedOnChange = useCallback(
    _debounce(props.onChange, 700, { leading: false, trailing: true }),
    []
  );

  useEffect(() => {
    if (props.initialValue) {
      setValue(props.initialValue);
      validateInput(props.initialValue);
    }
  }, []);

  useEffect(() => {
    if (hasError) {
      dispatch(setVoucherCustomAmountIsValid(false));
    }
    if (!hasError && value) {
      dispatch(setVoucherCustomAmountIsValid(true));
    }
    debouncedOnChange(value);
  }, [value]);

  const validateInput = (input: string) => {
    if (input === "") {
      const isValid = !props.required;
      setLocalValid(isValid);
      setHasError(props.required || false);
      return isValid;
    }

    const num = Number(input);
    const isValid = !isNaN(num) && num >= min && num <= max;
    setLocalValid(isValid);
    setHasError(!isValid);
    return isValid;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    // Allow empty string or numeric values
    if (input === "" || /^[0-9]*$/.test(input)) {
      setValue(input);
      validateInput(input);
    }
  };

  const handleBlur = () => {
    if (value === "") {
      setLocalValid(!props.required);
      setHasError(props.required || false);
      return;
    }

    const num = Number(value);
    if (isNaN(num)) {
      setValue("");
      setLocalValid(false);
      setHasError(true);
    } else if (num < min) {
      setValue(min.toString());
      setLocalValid(true);
      setHasError(false);
    } else if (num > max) {
      setValue(max.toString());
      setLocalValid(true);
      setHasError(false);
    }
  };

  const showError = hasError && (value !== "" || props.required);

  return (
    <StyledTextField
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      variant={"outlined"}
      label={props.label}
      color={"primary"}
      fullWidth={false}
      size={"medium"}
      required={props.required ?? true}
      autoComplete={props.autoComplete}
      error={showError}
      valid={props.valid || localValid}
      InputLabelProps={{
        style: {
          color: showError ? "#E94F54" : "rgba(0, 0, 0, 0.8)",
        },
      }}
      InputProps={{
        endAdornment:
          (props.valid || localValid) && !showError ? (
            <InputAdornment position="end">
              <CheckIcon sx={{ color: theme.palette.input.focus }} />
            </InputAdornment>
          ) : null,
        inputProps: {
          inputMode: "numeric",
          pattern: "[0-9]*",
          min,
          max,
        },
      }}
      helperText={
        showError ? `Please enter a number between ${min} and ${max}` : ""
      }
    />
  );
};

export default DebouncedNumberInput;
